// 在 Archive/index.jsx 的顶部，修改 import 语句
import React, { useState, useEffect } from 'react';
import { 
  Input, 
  Select, 
  Empty, 
  Spin, 
  Row, 
  Col, 
  Card, 
  Tag, 
  Space, 
  Statistic,
  Timeline,
  Button,
  Tooltip,
  Alert,
  Typography  // 添加这行！
} from 'antd';
import { 
  SearchOutlined, 
  CalendarOutlined, 
  EyeOutlined, 
  LikeOutlined, 
  UserOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  FileTextOutlined,
  StarOutlined,
  FireOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { mockPosts } from '../../api/mockData';
import './index.css';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;  // 从 Typography 中解构出 Text 组件

const Archive = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' 或 'timeline'

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = () => {
    setTimeout(() => {
      const savedPosts = JSON.parse(localStorage.getItem('blog_posts') || '[]');
      if (savedPosts.length > 0) {
        setPosts(savedPosts);
        setFilteredPosts(savedPosts);
      } else {
        setPosts(mockPosts);
        setFilteredPosts(mockPosts);
        localStorage.setItem('blog_posts', JSON.stringify(mockPosts));
      }
      setLoading(false);
    }, 500);
  };

  useEffect(() => {
    let result = [...posts];
    
    // 搜索过滤
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      result = result.filter(post => 
        post.title.toLowerCase().includes(lowerSearch) ||
        post.summary.toLowerCase().includes(lowerSearch) ||
        post.tags.some(tag => tag.toLowerCase().includes(lowerSearch))
      );
    }
    
    // 标签过滤
    if (selectedTag !== 'all') {
      result = result.filter(post => post.tags.includes(selectedTag));
    }
    
    // 年份过滤
    if (selectedYear !== 'all') {
      result = result.filter(post => {
        const postYear = post.date.split('-')[0];
        return postYear === selectedYear;
      });
    }
    
    // 排序
    result.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'views':
          aValue = a.views || 0;
          bValue = b.views || 0;
          break;
        case 'likes':
          aValue = a.likes || 0;
          bValue = b.likes || 0;
          break;
        case 'title':
          aValue = a.title;
          bValue = b.title;
          break;
        default:
          aValue = new Date(a.date);
          bValue = new Date(b.date);
      }
      
      if (sortOrder === 'desc') {
        return bValue - aValue || b.title.localeCompare(a.title);
      } else {
        return aValue - bValue || a.title.localeCompare(b.title);
      }
    });
    
    setFilteredPosts(result);
  }, [searchText, selectedTag, selectedYear, sortBy, sortOrder, posts]);

  // 获取所有标签
  const allTags = Array.from(new Set(posts.flatMap(post => post.tags)));
  
  // 获取所有年份
  const allYears = Array.from(new Set(posts.map(post => post.date.split('-')[0]))).sort((a, b) => b - a);
  
  // 统计信息
  const totalViews = posts.reduce((sum, post) => sum + (post.views || 0), 0);
  const totalLikes = posts.reduce((sum, post) => sum + (post.likes || 0), 0);
  const mostPopularPost = posts.length > 0 
    ? [...posts].sort((a, b) => (b.views || 0) - (a.views || 0))[0]
    : null;

  if (loading) {
    return (
      <div className="archive-loading">
        <Spin size="large" tip="正在加载文章档案..." />
        <p className="loading-text">请稍候，我们正在整理你的记忆</p>
      </div>
    );
  }

  return (
    <div className="archive-page">
      {/* 页面头部 */}
      <div className="archive-header">
        <div className="header-content">
          <h1 className="page-title">
            <FileTextOutlined className="title-icon" />
            文章档案馆
          </h1>
          <p className="page-subtitle">这里收藏着 {posts.length} 篇时光印记</p>
        </div>
        
        <div className="stats-summary">
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Statistic 
                title="文章总数" 
                value={posts.length} 
                prefix={<FileTextOutlined />}
                className="stat-item"
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic 
                title="总浏览量" 
                value={totalViews} 
                prefix={<EyeOutlined />}
                className="stat-item"
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic 
                title="总点赞数" 
                value={totalLikes} 
                prefix={<LikeOutlined />}
                className="stat-item"
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic 
                title="时间跨度" 
                value={allYears.length || 1} 
                suffix="年"
                prefix={<CalendarOutlined />}
                className="stat-item"
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* 热门文章提示 */}
      {mostPopularPost && (
        <Alert
          message={
            <Space>
              <FireOutlined />
              <span>热门文章：</span>
              <Link to={`/post/${mostPopularPost.id}`} className="popular-post-link">
                {mostPopularPost.title}
              </Link>
              <Tag color="red" icon={<EyeOutlined />}>{mostPopularPost.views} 浏览</Tag>
            </Space>
          }
          type="info"
          showIcon
          className="popular-alert"
        />
      )}

      {/* 控制面板 */}
      <Card className="control-panel">
        <div className="controls-grid">
          <div className="control-group">
            <label><SearchOutlined /> 搜索文章</label>
            <Search
              placeholder="搜索标题、内容或标签..."
              prefix={<SearchOutlined />}
              onChange={e => setSearchText(e.target.value)}
              allowClear
              className="search-control"
            />
          </div>
          
          <div className="control-group">
            <label><FilterOutlined /> 标签筛选</label>
            <Select
              value={selectedTag}
              onChange={setSelectedTag}
              placeholder="选择标签"
              className="filter-control"
              dropdownMatchSelectWidth={false}
            >
              <Option value="all">全部标签</Option>
              {allTags.map(tag => (
                <Option key={tag} value={tag}>
                  <Tag color="blue">{tag}</Tag>
                </Option>
              ))}
            </Select>
          </div>
          
          <div className="control-group">
            <label><CalendarOutlined /> 年份筛选</label>
            <Select
              value={selectedYear}
              onChange={setSelectedYear}
              placeholder="选择年份"
              className="filter-control"
            >
              <Option value="all">全部年份</Option>
              {allYears.map(year => (
                <Option key={year} value={year}>{year} 年</Option>
              ))}
            </Select>
          </div>
          
          <div className="control-group">
            <label><SortAscendingOutlined /> 排序方式</label>
            <div className="sort-controls">
              <Select
                value={sortBy}
                onChange={setSortBy}
                className="sort-select"
              >
                <Option value="date">发布日期</Option>
                <Option value="views">浏览最多</Option>
                <Option value="likes">点赞最多</Option>
                <Option value="title">标题顺序</Option>
              </Select>
              <Tooltip title={sortOrder === 'desc' ? '降序排列' : '升序排列'}>
                <Button
                  icon={sortOrder === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  className="sort-order-btn"
                />
              </Tooltip>
            </div>
          </div>
          
          <div className="control-group">
            <label>视图模式</label>
            <Space className="view-toggle">
              <Button
                type={viewMode === 'grid' ? 'primary' : 'default'}
                icon={<FileTextOutlined />}
                onClick={() => setViewMode('grid')}
              >
                网格视图
              </Button>
              <Button
                type={viewMode === 'timeline' ? 'primary' : 'default'}
                icon={<CalendarOutlined />}
                onClick={() => setViewMode('timeline')}
              >
                时间线
              </Button>
            </Space>
          </div>
        </div>
        
        <div className="active-filters">
          <Space wrap>
            {selectedTag !== 'all' && (
              <Tag closable onClose={() => setSelectedTag('all')}>
                标签：{selectedTag}
              </Tag>
            )}
            {selectedYear !== 'all' && (
              <Tag closable onClose={() => setSelectedYear('all')}>
                年份：{selectedYear}
              </Tag>
            )}
            {searchText && (
              <Tag closable onClose={() => setSearchText('')}>
                搜索：{searchText}
              </Tag>
            )}
          </Space>
        </div>
      </Card>

      {/* 内容区域 */}
      <div className="content-area">
        {filteredPosts.length === 0 ? (
          <Empty 
            description={
              <div className="empty-state">
                <h3>没有找到符合条件的文章</h3>
                <p>尝试调整筛选条件或搜索关键词</p>
                <Button 
                  type="primary" 
                  onClick={() => {
                    setSearchText('');
                    setSelectedTag('all');
                    setSelectedYear('all');
                  }}
                >
                  重置所有筛选
                </Button>
              </div>
            }
            imageStyle={{ height: 180 }}
            className="empty-container"
          />
        ) : viewMode === 'grid' ? (
          <>
            <div className="results-header">
              <h3>找到 {filteredPosts.length} 篇文章</h3>
              <Text type="secondary">当前排序：{
                sortBy === 'date' ? '发布日期' : 
                sortBy === 'views' ? '浏览最多' : 
                sortBy === 'likes' ? '点赞最多' : '标题顺序'
              } ({sortOrder === 'desc' ? '最新优先' : '最早优先'})</Text>
            </div>
            
            <Row gutter={[24, 24]} className="posts-grid">
              {filteredPosts.map((post, index) => (
                <Col key={post.id} xs={24} md={12} lg={8}>
                  <div 
                    className="archive-card-wrapper"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <Link to={`/post/${post.id}`} className="card-link">
                      <Card 
                        hoverable
                        className="archive-card"
                        cover={
                          <div className="card-cover">
                            <div 
                              className="cover-gradient"
                              style={{
                                background: `linear-gradient(135deg, 
                                  hsl(${(post.id * 137) % 360}, 70%, 50%) 0%, 
                                  hsl(${(post.id * 137 + 60) % 360}, 70%, 50%) 100%)`
                              }}
                            >
                              <span className="cover-text">{post.title.substring(0, 2)}</span>
                            </div>
                          </div>
                        }
                      >
                        <div className="card-content">
                          <div className="card-header">
                            <h3 className="card-title">{post.title}</h3>
                            {post.tags.includes('热门') && (
                              <StarOutlined className="featured-icon" />
                            )}
                          </div>
                          
                          <p className="card-summary">{post.summary}</p>
                          
                          <div className="card-tags">
                            {post.tags.slice(0, 3).map(tag => (
                              <Tag key={tag} color="blue" size="small">{tag}</Tag>
                            ))}
                            {post.tags.length > 3 && (
                              <Tag size="small">+{post.tags.length - 3}</Tag>
                            )}
                          </div>
                          
                          <div className="card-footer">
                            <Space size="middle" split={<span className="divider">•</span>}>
                              <span className="footer-item">
                                <UserOutlined />
                                <span>{post.author}</span>
                              </span>
                              <span className="footer-item">
                                <CalendarOutlined />
                                <span>{post.date}</span>
                              </span>
                              <span className="footer-item">
                                <EyeOutlined />
                                <span>{post.views}</span>
                              </span>
                              <span className="footer-item">
                                <LikeOutlined />
                                <span>{post.likes}</span>
                              </span>
                            </Space>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </div>
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <div className="timeline-view">
            <div className="timeline-header">
              <h3>时间线视图</h3>
              <Text type="secondary">按时间顺序排列</Text>
            </div>
            
            <Timeline mode="alternate" className="custom-timeline">
              {filteredPosts.map((post, index) => (
                <Timeline.Item
                  key={post.id}
                  color={index % 2 === 0 ? '#1890ff' : '#52c41a'}
                  dot={<CalendarOutlined style={{ fontSize: '16px' }} />}
                >
                  <div className="timeline-card">
                    <div className="timeline-date">{post.date}</div>
                    <Link to={`/post/${post.id}`} className="timeline-title">
                      {post.title}
                    </Link>
                    <p className="timeline-summary">{post.summary}</p>
                    <div className="timeline-tags">
                      {post.tags.map(tag => (
                        <Tag key={tag} size="small">{tag}</Tag>
                      ))}
                    </div>
                    <div className="timeline-meta">
                      <Space size="small">
                        <span><UserOutlined /> {post.author}</span>
                        <span><EyeOutlined /> {post.views}</span>
                        <span><LikeOutlined /> {post.likes}</span>
                      </Space>
                    </div>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </div>
        )}
      </div>

      {/* 底部信息 */}
      <div className="archive-footer">
        <div className="footer-content">
          <p>
            <FileTextOutlined /> 共归档 {posts.length} 篇文章 • 
            <CalendarOutlined /> 时间跨度 {allYears.length || 1} 年 •
            <Tag color="blue">最后更新：今天</Tag>
          </p>
          <Button 
            type="link" 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            ↑ 回到顶部
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Archive;

